// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// «ACTUALIZAR» — UM componente, em todas as abas
// ============================================================================
//
// 📗 Cliente: *«em todas as abas tem de ter a opção actualizar, para actualizar por vezes os
//    dados»*, com a regra base *«TODAS AS CORRECÇÕES TÊM DE SER CORRIGIDAS, EFECTIVADAS E
//    TESTADAS EM TODAS AS ABAS DO SISTEMA QUE ESTIVEREM ENVOLVIDAS»*.
//
// ⚠️⚠️ **UM COMPONENTE E NÃO UM BOTÃO POR ABA.** Este projecto tem a lição registada dezenas de
//    vezes — *«a formulação que divergir é a que ninguém corre»*. Onze janelas com onze botões
//    dariam onze comportamentos: uma sem estado de carregamento, outra sem data, outra a perder
//    o filtro.
//    📌 A prova de que são mesmo **todas** não é este ficheiro: é o guarda estrutural
//    `actualizar-em-todas-as-abas.test.ts`, que lê o código de cada janela e recusa a que não
//    o usar. Uma janela nova nasce sem botão, e o portão diz o nome dela.
//
// ---------------------------------------------------------------------------
// ⚠️⚠️ AS TRÊS COISAS QUE ELE TEM DE FAZER BEM — e porque é que cada uma existe
// ---------------------------------------------------------------------------
//
//   1 · **DIZER QUE ESTÁ A CARREGAR.** Sem isso, carregar duas vezes é o comportamento normal
//       — e duas recargas concorrentes fazem a resposta mais lenta chegar depois da mais
//       rápida, logo o ecrã acaba a mostrar os dados **mais antigos**. O botão fica
//       `disabled` enquanto corre, e diz «a actualizar…».
//
//   2 · ⚠️ **NÃO PERDER O QUE O UTILIZADOR ESCREVEU.** Recarregar é para os **dados**, não
//       para o **estado do ecrã**. Por isso este componente **não sabe recarregar nada**:
//       recebe a função que a janela já usa (`carregar`, `recarregarLista`, o contador) e
//       chama-a. Não desmonta, não repõe filtros, não mexe em selectores.
//       📌 É também o que o torna seguro no `Gestao`, onde bater no separador **repõe** a
//       procura e a ordenação: lá chama-se `carregar(aba)` e não se toca no separador.
//
//   3 · **DIZER QUANDO FOI, OU PORQUE É QUE UM CLIQUE NÃO MUDOU NADA.** Um botão que parece
//       não fazer nada é indistinguível de um botão avariado — e uma lista que já estava
//       actualizada é exactamente o caso em que ele parece não fazer nada.
//
// ---------------------------------------------------------------------------
// ⚠️⚠️ «SEM ALTERAÇÕES» SÓ SE DIZ COM PROVA — e é por isso que a assinatura é OPCIONAL
// ---------------------------------------------------------------------------
//
//   Dizer «sem alterações» sobre dados que mudaram é pior do que não dizer nada: manda o
//   planeador confiar num ecrã desactualizado. 📌 Uma assinatura fraca (o **número** de
//   linhas, por exemplo) mente exactamente assim — uma reserva que passa a confirmada não
//   muda a contagem. Por isso a frase **só aparece quando quem chama fornece uma assinatura
//   fiel** do que está no ecrã (tipicamente `() => JSON.stringify(dados)`); sem ela mostra-se
//   a hora e mais nada, que é verdade em qualquer caso.
//
// ⚠️ **A assinatura é uma FUNÇÃO e não um valor**: como valor teria de ser recalculada em
//    cada render de uma janela que redesenha ao passar o rato. Assim só corre duas vezes por
//    clique.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';

export interface BotaoActualizarProps {
  /**
   * O que a janela já faz para ir buscar os dados — `carregar`, `recarregarLista`, ou o
   * incremento do contador que dispara o efeito.
   *
   * ⚠️ Pode devolver uma promessa ou nada. Quando devolve **nada** (o caso dos contadores),
   * não há como esperar pela resposta: ver `esperaPeloContador`.
   */
  readonly actualizar: () => void | Promise<unknown>;
  /**
   * Uma assinatura **fiel** do que está no ecrã, para distinguir «recarreguei e veio igual»
   * de «recarreguei e mudou». Sem ela não se afirma nenhuma das duas. Ver o cabeçalho.
   */
  readonly assinatura?: (() => string) | undefined;
  /**
   * ⚠️ Quando `actualizar` **não** devolve promessa (dispara um contador e volta logo), não
   * há nada por que esperar: o estado «a carregar» duraria um instante e a comparação da
   * assinatura correria antes de a resposta chegar. Nesse caso espera-se este número de
   * milissegundos antes de comparar — e sem assinatura não se compara de todo.
   */
  readonly esperaPeloContador?: number;
}

/** Quanto tempo a frase «sem alterações» / «actualizado» fica no ecrã. */
const MOSTRAR_MS = 6000;

/**
 * ⚠️ A hora é a do **navegador**, e é a hora certa para esta frase: ela responde a «há quanto
 * tempo é que EU carreguei nisto», que é um facto local. As datas dos **dados** continuam a
 * vir do servidor (o `hoje` da base), como em `Atencao`.
 */
function horaCurta(d: Date): string {
  return d.toLocaleTimeString('pt-PT', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function BotaoActualizar({
  actualizar,
  assinatura,
  esperaPeloContador = 400,
}: BotaoActualizarProps) {
  const [aCarregar, definirACarregar] = useState(false);
  /** `null` enquanto ninguém carregou neste ecrã — e então não se afirma hora nenhuma. */
  const [quando, definirQuando] = useState<Date | null>(null);
  const [semAlteracoes, definirSemAlteracoes] = useState(false);
  const [falhou, definirFalhou] = useState(false);

  /**
   * ⚠️ A assinatura lê-se pela **referência** e não pela closure: quando o efeito de
   * comparação corre, o `render` já é o novo, mas a função `assinatura` que esta closure
   * capturou podia ser a antiga. A ref é sempre a da última renderização.
   */
  const assinaturaRef = useRef(assinatura);
  assinaturaRef.current = assinatura;
  const antes = useRef<string | null>(null);
  /**
   * ⚠️⚠️ **A COMPARAÇÃO NÃO PODE ACONTECER LOGO A SEGUIR AO `await`.** Quem carrega faz
   * `definirDados(await r.json())` — o `setState` do PAI é despachado, mas a re-renderização
   * ainda não aconteceu, logo ler a assinatura ali dava sempre o valor **antigo** e o ecrã
   * dizia «sem alterações» em todas as recargas. 📌 Passa-se a `aComparar`, que provoca uma
   * renderização; nessa, os dados do pai já entraram (mesmo lote) e o efeito lê o valor novo.
   */
  const [aComparar, definirAComparar] = useState(0);

  useEffect(() => {
    if (aComparar === 0) return undefined;
    const agora = assinaturaRef.current?.();
    definirSemAlteracoes(antes.current !== null && agora !== undefined && antes.current === agora);
    definirQuando(new Date());
    return undefined;
  }, [aComparar]);

  // ⚠️ A frase apaga-se sozinha, pela mesma razão do realce do mapa: uma marca permanente
  //    vira decoração, e ao terceiro clique ninguém sabe a que clique ela se refere.
  useEffect(() => {
    if (quando === null) return undefined;
    const t = window.setTimeout(() => definirSemAlteracoes(false), MOSTRAR_MS);
    return () => window.clearTimeout(t);
  }, [quando]);

  const clicar = useCallback(async () => {
    if (aCarregar) return;
    antes.current = assinaturaRef.current?.() ?? null;
    definirACarregar(true);
    definirSemAlteracoes(false);
    definirFalhou(false);
    try {
      const r = actualizar();
      if (r instanceof Promise) {
        await r;
      } else if (assinaturaRef.current !== undefined) {
        // Ver `esperaPeloContador`: sem promessa não há resposta por que esperar.
        await new Promise((s) => window.setTimeout(s, esperaPeloContador));
      }
    } catch {
      // ⚠️ **Não se engole.** Quem chama trata os seus próprios erros de rede e escreve-os na
      //   janela; o que este botão tem de dizer é que **o clique não completou** — senão o
      //   silêncio lê-se como «actualizado».
      definirFalhou(true);
      definirACarregar(false);
      definirQuando(new Date());
      return;
    }
    definirACarregar(false);
    definirAComparar((n) => n + 1);
  }, [aCarregar, actualizar, esperaPeloContador]);

  return (
    <span className="actualizar">
      <button
        type="button"
        className="btn btn--peq"
        onClick={() => void clicar()}
        disabled={aCarregar}
        aria-busy={aCarregar}
        title={
          'vai buscar os dados outra vez ao servidor — não mexe nos filtros nem no que estiver ' +
          'a meio de preencher'
        }
      >
        {aCarregar ? 'a actualizar…' : 'Actualizar'}
      </button>
      {/* ⚠️ **`aria-live` e NAO `role="status"`.** Quem usa leitor de ecra tem de saber que o
          clique produziu alguma coisa — e as duas formas anunciam igual (`status` implica
          `aria-live="polite"`). ⚠️⚠️ Mas o `role` acrescenta um segundo elemento com o papel
          `status` a cada janela, e isso **partiu o `Contas.test.tsx`** («Found multiple
          elements with the role status»): o ecra das contas ja tem o seu, e a prova que o
          procurava passou a encontrar dois. Um controlo partilhado por onze janelas nao pode
          trazer um papel de ARIA que essas janelas ja usem. */}
      <span className="actualizar-quando" aria-live="polite">
        {falhou
          ? 'não deu — tente outra vez'
          : quando === null
            ? ''
            : semAlteracoes
              ? `sem alterações · ${horaCurta(quando)}`
              : `actualizado às ${horaCurta(quando)}`}
      </span>
    </span>
  );
}
